import { LightbulbOutlined as LightbulbOutlinedIcon } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import FormField from "~/components/Project/formField";
import { CommonButton } from "~/components/ui/commonButton";
import { TOAST_MESSAGES } from "~/constants/messages/toastMessages";
import { KintoneAdminApi, type KintoneAdminInfo } from "~/functions/api/kintoneAdmin";
import styles from "./index.module.scss";

interface KintoneAdminSettingsProps {
  organizationId: string;
  projectId: string;
}

export const KintoneAdminSettings: React.FC<KintoneAdminSettingsProps> = ({
  organizationId,
  projectId,
}) => {
  const kintoneAdminApi = useRef(new KintoneAdminApi());
  const [adminInfo, setAdminInfo] = useState<KintoneAdminInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({ subdomain: "", login_id: "", password: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAdmin = async () => {
      setIsLoading(true);
      const info = await kintoneAdminApi.current.getKintoneAdmin(organizationId, projectId);
      setAdminInfo(info);
      setIsLoading(false);
    };
    fetchAdmin();
  }, [organizationId, projectId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleRegister = async () => {
    setIsSubmitting(true);
    setError(null);
    const result = await kintoneAdminApi.current.createKintoneAdmin(
      organizationId,
      projectId,
      formData,
    );
    if (result.success) {
      const info = await kintoneAdminApi.current.getKintoneAdmin(organizationId, projectId);
      setAdminInfo(info);
      setFormData({ subdomain: "", login_id: "", password: "" });
    } else {
      setError(result.error || TOAST_MESSAGES.KINTONE_ADMIN.REGISTER_FAILED);
    }
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    if (!adminInfo?.id) return;
    if (!window.confirm("kintone管理者情報を削除しますか？")) return;
    const success = await kintoneAdminApi.current.deleteKintoneAdmin(
      organizationId,
      projectId,
      adminInfo.id,
    );
    if (success) {
      setAdminInfo({ is_registered: false });
    }
  };

  const tipsSection = (
    <div className={styles["tipsSection"]}>
      <div className={styles["tipsTitle"]}>
        <LightbulbOutlinedIcon sx={{ width: 18, height: 18 }} />
        kintoneシステム管理者とは
      </div>
      <ul className={styles["tipsList"]}>
        <li className={styles["tipsItem"]}>
          kintone全体に関わる設定を管理できる権限を持つアカウントのことです
        </li>
        <li className={styles["tipsItem"]}>
          カスタマイズ仕様書に基づくJavaScript/CSSファイルのアップロードに必要です
        </li>
        <li className={styles["tipsItem"]}>
          確認方法: kintoneの設定（歯車アイコン）→ kintoneシステム管理 → アクセス権
          →「システムの管理」が有効なユーザー
        </li>
      </ul>
    </div>
  );

  const appGenerationLink = `/${organizationId}/project/${projectId}/app-generation?openModal=true`;

  const navigationGuide = (
    <p className={styles["description"]}>
      kintoneアプリ生成に使用するシステム管理者の認証情報を登録します。
      <br />
      アプリ生成機能を設定中の方は、
      <Link to={appGenerationLink} className={styles["navLink"]}>
        アプリ生成画面に戻る
      </Link>
    </p>
  );

  if (isLoading) return <div>読み込み中...</div>;

  const isFormValid = formData.subdomain && formData.login_id && formData.password;

  if (!adminInfo?.is_registered) {
    return (
      <div className={styles["container"]}>
        <h3 className={styles["title"]}>kintoneシステム管理者</h3>
        {navigationGuide}
        <div className={styles["body"]}>
          <div className={styles["main"]}>
            {error && <p className={styles["error"]}>{error}</p>}
            <div className={styles["form"]}>
              <FormField
                label="サブドメイン"
                name="subdomain"
                type="text"
                placeholder="サブドメインを入力"
                value={formData.subdomain}
                onChange={handleInputChange}
                required
                suffix=".cybozu.com"
              />
              <FormField
                label="ログイン名（kintone）"
                name="login_id"
                type="text"
                placeholder="ログイン名を入力"
                value={formData.login_id}
                onChange={handleInputChange}
                required
              />
              <FormField
                label="パスワード（kintone）"
                name="password"
                type="password"
                placeholder="パスワードを入力"
                value={formData.password}
                onChange={handleInputChange}
                required
              />
              <CommonButton onClick={handleRegister} disabled={isSubmitting || !isFormValid}>
                {isSubmitting ? "登録中..." : "登録"}
              </CommonButton>
            </div>
          </div>
          <aside className={styles["aside"]}>{tipsSection}</aside>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["container"]}>
      <h3 className={styles["title"]}>kintoneシステム管理者</h3>
      {navigationGuide}
      <div className={styles["body"]}>
        <div className={styles["main"]}>
          <div className={styles["info"]}>
            <div className={styles["field"]}>
              <span className={styles["fieldLabel"]}>サブドメイン</span>
              <span>{adminInfo.subdomain}</span>
            </div>
            <div className={styles["field"]}>
              <span className={styles["fieldLabel"]}>ログイン名（kintone）</span>
              <span>{adminInfo.login_id}</span>
            </div>
            <div className={styles["field"]}>
              <span className={styles["fieldLabel"]}>パスワード（kintone）</span>
              <span>&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;</span>
            </div>
            <div className={styles["actions"]}>
              <CommonButton variant="label" onClick={handleDelete}>
                削除
              </CommonButton>
            </div>
          </div>
        </div>
        <aside className={styles["aside"]}>{tipsSection}</aside>
      </div>
    </div>
  );
};
